<script setup lang="ts">
import { ref, watch } from 'vue'

import { Input } from '@/shared/ui/Input'

/**
 * A list edited as text, one entry per line — domains, ids, anything short enough that
 * a row of inputs would be worse than a paragraph.
 *
 * Blank lines are dropped and entries trimmed on the way out, so a trailing newline is
 * not an empty entry the API has to refuse. The text itself is kept locally rather than
 * derived back from the list: cleaning what is displayed would swallow the Enter the
 * user just pressed, since the empty line it opens is exactly what cleaning removes.
 */
const props = withDefaults(
  defineProps<{
    modelValue?: Array<string | number>
    rows?: number
    disabled?: boolean
    placeholder?: string
  }>(),
  { modelValue: () => [], rows: 4 }
)

const emit = defineEmits<{
  (e: 'update:modelValue', value: string[]): void
}>()

const toList = (text: string) =>
  text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)

const draft = ref(props.modelValue.join('\n'))

// Re-seeded only when the value that arrives is not the one this textarea already
// holds — a form reset or a record loading in, rather than the echo of our own emit.
watch(
  () => props.modelValue,
  (value) => {
    const incoming = value.join('\n')

    if (incoming !== toList(draft.value).join('\n')) draft.value = incoming
  }
)

function onInput(value: string | number) {
  draft.value = String(value)
  emit('update:modelValue', toList(draft.value))
}
</script>

<template>
  <Input
    :model-value="draft"
    type="textarea"
    :rows="rows"
    :disabled="disabled"
    :placeholder="placeholder"
    @update:model-value="onInput"
  />
</template>
